import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form, Field, FormikErrors, FormikHelpers } from 'formik';
import { Redirect } from 'react-router-dom';
import { getDealerId, setOtpForSession } from 'src/services/user.service';
import {
  setPasswordRoute,
  onboardingResetPasswordUserIdRoute,
  onboardingFirstTimeUser,
  onboardingBrokerRoutePos,
} from 'src/routing/routes';
import {
  FloatingInput,
  ShapesDecorativeBorder,
  ButtonLink,
  ShowIf,
  FullPageLoader,
} from 'src/components';
import TealiumEvents, {
  SetLoginType,
  UnsetResetJourney,
  SetResetJourney,
} from 'src/helpers/tealium-events';
import { SelfServiceFormBackground } from 'src/containers/self-service/self-service-form-background/self-service-form-background';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import { FormContainer } from 'src/containers/self-service/form-container/form-container';
import ButtonPrimary from 'src/containers/self-service/broker-journey/component/button-primary/button-primary';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import useBrokerLogin from 'src/hooks/useBrokerLogin';
import { InputField, useTealiumIdle } from 'src/features/common';
import './login.scss';

const SUBMISSION_ERRORS = {
  incorrectPassword: 'Your password or merchant ID is incorrect',
  invalidBroker: 'Broker is assigned to a different Sales Organization',
  notValidAgent: 'not valid agent',
  invalidCredentials: 'Invalid Credentials',
};

type BrokerLoginFormValues = {
  merchantId: string;
  password: string;
  dealerId?: { code: string; name: string };
};
enum UserOptions {
  FirstTimeUser = 'click here',
  ForgotPassword = 'forgot password',
}
export default function Login() {
  const handleTechnicalError = useTechnicalErrorHandler();
  const [redirectToRoute, setRedirectToRoute] = useState('');
  const agentJourneyDealerId = 'Vodacom Store Agent';
  const {
    authenticate,
    agentLogin,
    isLoading,
    salesOrganizations,
    hasDealerId,
    validateBroker,
    isValidBroker,
    broker,
    validateBrokerAgainstSalesOrg,
    getSalesOrgs,
    compareSalesOrgs,
  } = useBrokerLogin();

  const topOfPage = useRef<HTMLHeadingElement>(null);

  const { initFieldCount, sendIdleEvent } = useTealiumIdle(45_000);
  useEffect(() => {
    TealiumEvents.landOnLogin();
    if (!broker && !isValidBroker && (!salesOrganizations || salesOrganizations.length === 0)) {
      getSalesOrgs();
    }
  }, []);

  if (!!redirectToRoute) {
    return <Redirect push to={redirectToRoute} />;
  }

  const initialValues = () => {
    return {
      merchantId: '',
      password: '',
    };
  };

  return (
    <div className="broker-login">
      <SelfServiceFormBackground className={'dark-background'}>
        <FullPageLoader showLoader={isLoading} />
        <ShapesDecorativeBorder>
          <section className="broker-login-form">
            <Formik<BrokerLoginFormValues>
              initialValues={initialValues()}
              validate={validateLoginForm}
              onSubmit={submitLoginForm}
              validateOnChange={true}
              validateOnBlur={false}
              enableReinitialize={true}
            >
              {({ isSubmitting, values }) => {
                if (
                  isSubmitting &&
                  values.dealerId &&
                  values.dealerId.code !== agentJourneyDealerId
                ) {
                  localStorage.setItem('sales_org', values.dealerId.code);
                }

                return (
                  <FormContainer classname={'container-white'}>
                    <Form>
                      <div className="has-text-centered has-padding-top-7">
                        <h4 className="form-heading heading-four" ref={topOfPage}>
                          Log in to your account
                        </h4>
                      </div>
                      {initFieldCount<BrokerLoginFormValues>(values)}
                      {sendIdleEvent<BrokerLoginFormValues>(
                        values,
                        TealiumEvents.spendMorethan45sOnLogin,
                      )}

                      <ShowIf
                        condition={(!hasDealerId && salesOrganizations.length > 0) || !broker}
                      >
                        <Field
                          name="dealerId"
                          type="text"
                          placeholder="Select dealership"
                          items={salesOrganizations.sort(compareSalesOrgs)}
                          getItemDisplayValue={(item) => item.name}
                          component={InputField}
                          onItemSelect={(item) => validateBroker(item.code)}
                          fieldType="select"
                          isDark={false}
                        />
                      </ShowIf>
                      <Field
                        name="merchantId"
                        type="text"
                        placeholder="User ID"
                        component={FloatingInput}
                        fieldType="standard"
                        className="id-field"
                        spacing="has-margin-top-4"
                        automationIdPrefix="Login_Input_MerchantID"
                        autoFocus
                      />
                      <Field
                        name="password"
                        type="password"
                        component={FloatingInput}
                        className="password-field"
                        fieldType="standard"
                        placeholder="Password"
                        automationIdPrefix="Login_Input_Password"
                      />

                      <div className="has-text-centered first-time-member-text">
                        <span className="sign-up-text content-normal">First time member?</span>
                        &nbsp;
                        <ButtonLink
                          automationid="Login_Link_First_Time_Login"
                          onClick={() => {
                            TealiumEvents.userClicksLogin(UserOptions.FirstTimeUser);
                            setRedirectToRoute(onboardingFirstTimeUser);
                            SetResetJourney('first time login');
                          }}
                          disabled={isSubmitting}
                        >
                          Click here
                        </ButtonLink>
                      </div>

                      <div>
                        <ButtonPrimary
                          type="submit"
                          automationid="Login_Button_Login"
                          className="login-button"
                          disabled={isSubmitting || !isFormValid(values, hasDealerId)}
                        >
                          Login
                        </ButtonPrimary>
                      </div>

                      <div className="has-text-centered forgot-password-text">
                        <ButtonLink
                          automationid="Login_Link_Request_Reset"
                          onClick={() => {
                            if (!isSubmitting) {
                              TealiumEvents.userClicksLogin(UserOptions.ForgotPassword);
                              setRedirectToRoute(onboardingResetPasswordUserIdRoute);
                              UnsetResetJourney();
                            }
                          }}
                          disabled={isSubmitting}
                        >
                          Forgot password?
                        </ButtonLink>
                      </div>
                    </Form>
                  </FormContainer>
                );
              }}
            </Formik>
          </section>
        </ShapesDecorativeBorder>
      </SelfServiceFormBackground>
    </div>
  );

  function handleSuccessfulFirstTimeLogin(password: string) {
    TealiumEvents.loginClickLogin(setPasswordRoute);

    SetResetJourney('first time login');
    setOtpForSession(password);
    setRedirectToRoute(onboardingBrokerRoutePos);
  }

  async function handleSuccessfulLogin() {
    TealiumEvents.LoginSuccessfully();
    setRedirectToRoute(onboardingBrokerRoutePos);
  }

  function handleBadLogin(message) {
    TealiumEvents.LoginFailed(message, '401');
    TealiumEvents.LoginPageError(message, '401');
    toastMessageServiceSingleton.addError(message, null, '401', true, null, true);
  }

  async function submitLoginForm(
    values: BrokerLoginFormValues,
    helpers: FormikHelpers<BrokerLoginFormValues>,
  ) {
    const { setSubmitting, resetForm } = helpers;
    const params = {
      ...values,
      showToastOnError: false,
      suppressTealiumEvent: true,
    };

    let firstTimeLogin, userId, error;

    if (values?.dealerId && values?.dealerId.code === agentJourneyDealerId) {
      ({ firstTimeLogin, userId, error } = await agentLogin({
        ...params,
        agentId: values.merchantId,
      }));
    } else {
      ({ firstTimeLogin, userId, error } = await authenticate(params));
    }

    if (!error && values?.dealerId && values.dealerId.code !== agentJourneyDealerId) {
      error = await validateBrokerAgainstSalesOrg(
        broker?.code || getDealerId(),
        params.merchantId,
        userId,
      );
    }

    SetLoginType('Password');
    setSubmitting(false);

    switch (true) {
      case error === null || error === undefined:
        firstTimeLogin ? handleSuccessfulFirstTimeLogin(values.password) : handleSuccessfulLogin();
        break;
      case error?.includes(SUBMISSION_ERRORS.incorrectPassword):
      case error?.includes(SUBMISSION_ERRORS.invalidCredentials):
        handleBadLogin('Invalid User ID or Password');
        resetForm();
        break;
      case error?.includes(SUBMISSION_ERRORS.invalidBroker):
        handleBadLogin('Broker is assigned to a different Sales Organization');
        resetForm();
        break;
      case error?.includes(SUBMISSION_ERRORS.notValidAgent):
        handleBadLogin('Agent is not valid');
        resetForm();
        break;
      default:
        // @ts-ignore
        handleTechnicalError('3PO: unrecognized error received in response to login POST request');
    }
  }

  function isFormValid(values: BrokerLoginFormValues, isDealerId: boolean) {
    if (!isDealerId) {
      return values.merchantId && values.password && values.dealerId;
    }

    return values.merchantId && values.password;
  }

  function validateLoginForm(values: BrokerLoginFormValues) {
    const errors: FormikErrors<BrokerLoginFormValues> = {};
    if (!values.merchantId) {
      errors.merchantId = 'Your user ID is incorrect';
    }

    if (!values.password) {
      errors.password = 'Your password is incorrect';
    }
    return errors;
  }
}
